/**
 * adapter.ts — the single interface every backend implements.
 *
 * The frontend never branches on which adapter is live. The composer / play
 * controls call `loadScenario`, `subscribe`, `setParam`, `start`, `stop` and
 * don't care whether ticks come from an in-browser compute engine or a
 * remote API.
 *
 * Implementations:
 * - `LocalAdapter`     — the editable, in-browser compute engine. Runs
 *                        `computeTick` on a setInterval.
 * - `LazyLocalAdapter` — async-loading shell around LocalAdapter.
 * - `ApiAdapter`       — WebSocket client to a remote backend (the FastAPI
 *                        server's /ws). See `ApiAdapter.ts` and the wire
 *                        format in `types.ts`.
 */

import type {
  EdgeConnection,
  Bend,
  GraphEdge,
  GraphNode,
  KnobPin,
  LegendEntry,
  MetricSpec,
  NodeGroup,
  ParamSpec,
  ProjectBundle,
  ScenarioBundle,
  TickPayload,
  WorkflowTrigger,
} from './types';
import { MAIN_GRAPH_ID } from './types';
import {
  computeProjectTickSplit,
  resolveInitialParams,
  type PerGraphSimState,
  type ResolvedParams,
} from './compute';
import { getDefaultStorage } from '../storage/LocalStorageBackend';
import { applyBindingPatches, resolveCellBindings, type BindingTableSnapshot } from './bindings';

/** The interface every backend implements. */
export interface BackendAdapter {
  /** Load the project (the full graph collection). Called once on mount;
   *  resolves with the `ProjectBundle`. The LocalAdapter's graphs are mutable
   *  (edits flow back through the editor methods below); the WS adapter
   *  serves a snapshot. */
  loadScenario(): Promise<ProjectBundle>;
  /** Register a callback for each incoming tick. Returns an unsubscribe fn.
   *  Callbacks fire on the adapter's schedule (local: a setInterval the
   *  start/stop methods control; WS adapter: on each inbound message). */
  subscribe(onTick: (tick: TickPayload) => void): () => void;
  /** Register a callback fired whenever the bundle changes (an edit landed).
   *  Local-only; the WS adapter never fires this (its bundle is static). */
  onBundleChange?(cb: () => void): () => void;
  /** Register a callback fired when the engine throws during a tick (a
   *  CycleError from a feedback loop, or an ExprError from a bad formula).
   *  Local-only. The sim auto-stops on error; the UI surfaces the message. */
  onError?(cb: (message: string) => void): () => void;
  /** Register a callback fired on an inbound knob-change broadcast — either
   *  a peer's `setParam` (other tabs on the same scenario) or an agent's
   *  fenced `paramDelta` (the Phase D relay path). ApiAdapter-only; the local
   *  adapters apply setParam directly to their own param map and have no
   *  broadcast. The closed control loop's feedback half: an external model
   *  that drives the relay reads these to decide its next tick. */
  onParamChange?(cb: (path: string, value: number) => void): () => void;
  /** Register a callback fired when the backend sends a state snapshot
   *  (`{kind:'state'}`) — sent to a freshly-connected relay client so it can
   *  hydrate at the run's current position instead of starting from step 0
   *  (which would fail conservation on the first tick). ApiAdapter-only; the
   *  local adapters start from the bundle's initialStocks and need no hydrate.
   *  `step` is the next tick's step number; `stocks` is the conservation
   *  baseline (prev stocks) for that tick. */
  onStateHydrate?(cb: (step: number, stocks: Record<string, number>) => void): () => void;
  /** Push a knob change. Meaningful for Local (recomputes the next tick)
   *  and WS (forwards upstream). */
  setParam(path: string, value: number): void;
  /** Feed the latest project-table snapshots to the local engine so it can
   *  resolve cell-bound params (ParamSpec.binding / edge.weightBinding) into
   *  its param overlays — the in-browser half of "DB writes drive the graph".
   *  The snapshots come from the tables poll (useSim.tablesState); null means
   *  "no tables loaded" (bound params fall back to their saved defaults).
   *  ApiAdapter doesn't implement this: in relay mode the BACKEND resolves
   *  bindings per tick and ships the values in tick.params / paramChange. */
  updateTableSnapshots?(snapshots: readonly BindingTableSnapshot[] | null): void;
  /** Rename a param key on a node or edge. Local-only. Rewrites every
   *  reference in the bundle (EdgeConnection.sourceParam, source.rateParam,
   *  metric `param:`/`state:` quantities) so the rename doesn't break the
   *  running sim. Editor validates uniqueness before calling this. */
  setParamKey?(target: 'node' | 'edge', id: string, oldKey: string, newKey: string): void;
  /** ─── Editor operations (LocalAdapter implements these; File/WS noop). ───
   *  Each returns nothing; the LocalAdapter fires `onBundleChange` so the UI
   *  re-renders the graph/sidebar from the updated bundle. */
  addNode?(node: GraphNode): void;
  updateNode?(node: GraphNode): void;
  removeNode?(nodeId: string): void;
  /** Clone a node (deep copy of params + gate) with a new unique id and an
   *  offset position. Edges are NOT cloned — the user reconnects them.
   *  Local-only. Returns the new id (or null if the source wasn't found). */
  duplicateNode?(nodeId: string): string | null;
  /** Rename a node's id, rewriting every `edge.source` / `edge.target` /
   *  `metric.nodeId` reference in the bundle. Returns false (no-op) if the
   *  new id is already in use; the editor is expected to validate first.
   *  Local-only. */
  renameNode?(oldId: string, newId: string): boolean;
  addEdge?(edge: GraphEdge): void;
  updateEdge?(edge: GraphEdge): void;
  removeEdge?(edgeId: string): void;
  /** Rename an edge id, rewriting the edge id key in params/stocks + every
   *  EdgeConnection.sourceParam that pointed at the old id (for derive
   *  connections). Returns false if the new id is already in use. Local-only. */
  renameEdge?(oldId: string, newId: string): boolean;
  /** Set/clear the edge's `group` label. Local-only. */
  setEdgeGroup?(edgeId: string, group: string | undefined): void;
  /** Re-anchor an edge's source/target node + handle (which side of the node
   *  the edge attaches to). Used by React Flow's edge-reconnect drag and by
   *  the EdgeModal's side dropdowns. Local-only. */
  updateEdgeHandles?(
    edgeId: string,
    next: { source?: string; target?: string; sourceHandle?: string; targetHandle?: string },
  ): void;
  /** Replace the edge's user-authored bend points. Pass an empty array to
   *  clear. Each bend has { x, y, mode } where mode controls the curve at the
   *  point ('default' | 'smooth' | 'sharp'). Local-only. */
  setBends?(edgeId: string, bends: Bend[]): void;
  /** Set the weight label's position along the curve, as t ∈ [0,1].
   *  Local-only. */
  setLabelT?(edgeId: string, t: number): void;
  /** Add a group label to the bundle's known-groups list (the autocomplete
   *  source). Idempotent. Local-only. */
  addGroup?(group: string): void;
  /** ─── Spatial node groups (rectangles behind the graph) ───
   *  Membership is derived at render time from positions; these ops only
   *  manage the group entities themselves. Local-only. */
  addNodeGroup?(group: NodeGroup): void;
  updateNodeGroup?(group: NodeGroup): void;
  removeNodeGroup?(groupId: string): void;
  /** Rename a group's id. Membership is spatial so there are no references to
   *  rewrite. Returns false if the new id is already in use. Local-only. */
  renameGroup?(oldId: string, newId: string): boolean;
  addMetric?(metric: MetricSpec): void;
  updateMetric?(metric: MetricSpec): void;
  removeMetric?(key: string): void;
  /** Rename a metric's key in place (preserves order + other fields). */
  renameMetric?(oldKey: string, newKey: string): void;
  /** Replace the bundle's whole legend list. Cosmetic — does not affect the
   *  sim. Replaces the list wholesale (no per-entry add/remove/rename ops),
   *  matching how the sidebar edits the legend in place. */
  setLegend?(legend: LegendEntry[]): void;
  /** Replace the bundle's whole pinned-knobs list. Cosmetic metadata — slider
   *  drags dispatch through `setParam` (graph pins) or the tables PATCH route
   *  (db pins). Wholesale replace like `setLegend`. Empty list stored as
   *  `undefined`. */
  setKnobs?(knobs: KnobPin[]): void;
  /** Replace the bundle's whole workflow-trigger list (the trigger
   *  contract). Wholesale replace like `setLegend`; empty list stored as
   *  `undefined`. */
  setWorkflowTriggers?(triggers: WorkflowTrigger[]): void;
  /** Replace the whole bundle (used by the Composer to start a fresh graph).
   *  Multi-graph: targets the active graph's bundle. */
  replaceBundle?(bundle: ScenarioBundle): void;
  /** Export the current editable bundle to a serialisable ScenarioBundle
   *  (for the future "save scenario" feature). Multi-graph: the active graph. */
  exportBundle?(): ScenarioBundle;
  // ─── Multi-graph (graph collection) ──────────────────────────────────────
  /** The graph ids in the project (Main first). Static for File/Api; reflects
   *  edits for Local. */
  graphIds?(): string[];
  /** The currently-active graph id (the one the editor canvas shows). */
  activeGraphId?(): string;
  /** Switch the active graph (which one the editor shows + mutates). Local-only;
   *  a per-client UI choice, not broadcast. */
  setActiveGraph?(graphId: string): void;
  /** Add a new graph (collection op; broadcasts graphAdded over WS). */
  addGraph?(name: string): string;
  /** Remove a graph by id (refuses Main; broadcasts graphRemoved). */
  removeGraph?(graphId: string): void;
  /** Rename a graph (re-slugs the id for non-Main; broadcasts graphUpdate). */
  renameGraph?(graphId: string, name: string): string;
  /** The current full graph collection (synchronous snapshot). Local returns
   *  the live in-memory list; Api returns the latest handshake/broadcast state;
   *  File returns its single-graph project. Null before loadScenario. */
  getProject?(): ProjectBundle | null;
  /** Begin streaming ticks. Idempotent. */
  start(): void;
  /** Stop streaming ticks. Idempotent. Keeps the last frame; `start` resumes. */
  stop(): void;
  /** Reset stocks to the bundle's initialStocks (or 0) and step to 0. */
  reset?(): void;
  /** Tear down any open resources (timers, sockets). Called on unmount. */
  dispose(): void;
}

// ─── LocalAdapter (the editable, in-browser engine) ─────────────────────────
/**
 * Owns a mutable ScenarioBundle + resolved params + per-node stocks, and runs
 * `computeTick` on a setInterval to produce fresh ticks from the current
 * state. This is the adapter that makes the editor real: knob changes,
 * node/edge/metric edits all mutate the bundle, the next tick reflects them,
 * and `onBundleChange` fires so the UI re-renders the graph + sidebar.
 *
 * Use this for any session where the user is constructing or tweaking a
 * model (including read-only local views — canEdit gates the editor UI).
 */
export class LocalAdapter implements BackendAdapter {
  /** The active graph's live state — what the 24 editor methods mutate. Kept
   *  in lock-step with the per-graph maps via `syncActiveToMaps`/`loadActiveFromMaps`. */
  private bundle: ScenarioBundle;
  private params: ResolvedParams;
  private stocks: Record<string, number>;
  private step = 0;
  private readonly hz: number;
  private timer: ReturnType<typeof setInterval> | null = null;
  private subscribers = new Set<(tick: TickPayload) => void>();
  private bundleSubs = new Set<() => void>();
  private lastState: Record<string, Record<string, number>> | undefined = undefined;
  private errorSubs = new Set<(msg: string) => void>();
  /** The whole graph collection + per-graph sim state. The active graph's
   *  entry mirrors `bundle/params/stocks/lastState` (synced on every edit +
   *  at the start of each pump). */
  private bundles: ScenarioBundle[];
  private activeId: string;
  private paramsByGraph: Record<string, ResolvedParams> = {};
  private stocksByGraph: Record<string, Record<string, number>> = {};
  private prevStateByGraph: Record<string, Record<string, Record<string, number>> | undefined> = {};
  /** Latest project-table snapshots (useSim.tablesState) for cell-binding
   *  resolution. null until the first push — bound params fall back to their
   *  saved defaults until then. */
  private tableSnapshots: readonly BindingTableSnapshot[] | null = null;

  constructor(project: ProjectBundle | ScenarioBundle, opts: { hz?: number } = {}) {
    this.hz = opts.hz ?? 4;
    // Normalise: a bare ScenarioBundle is a legacy single-graph project (Main).
    const graphs = 'graphs' in project ? project.graphs : [asMain(project)];
    this.bundles = graphs.length > 0 ? graphs : [emptyMainBundle()];
    this.activeId = this.bundles[0].id;
    // Seed per-graph state; the active graph's state also lives in the
    // instance fields (loaded last).
    for (const b of this.bundles) {
      this.paramsByGraph[b.id] = resolveInitialParams(b);
      this.stocksByGraph[b.id] = { ...(b.initialStocks ?? {}) };
      this.prevStateByGraph[b.id] = undefined;
    }
    this.bundle = this.bundles[0];
    this.params = this.paramsByGraph[this.activeId];
    this.stocks = this.stocksByGraph[this.activeId];
    this.lastState = this.prevStateByGraph[this.activeId];
  }

  /** Build a LocalAdapter from a static scenario dir (loads graphs/main.json,
   *  falling back to legacy graph.json), editor enabled. */
  static async fromScenarioDir(
    scenarioId: string,
    opts: { hz?: number; base?: string } = {},
  ): Promise<LocalAdapter> {
    // Try the scenarioId as-is, then the underscore/hyphen alternate — the
    // public/scenarios/ dirs use underscores while project ids use hyphens.
    const base = opts.base ?? '/scenarios/';
    const candidates = [scenarioId, scenarioId.replace(/-/g, '_'), scenarioId.replace(/_/g, '-')];
    let lastErr: Error | null = null;
    for (const c of candidates) {
      const dir = `${base}${c}/`;
      try {
        const bundle = await fetchMainGraph(dir);
        if (bundle) return new LocalAdapter({ graphs: [asMain(bundle)] }, opts);
        lastErr = lastErr ?? new Error(`no graph in ${dir}`);
      } catch (e) {
        lastErr = e instanceof Error ? e : new Error(String(e));
      }
    }
    throw lastErr ?? new Error(`Could not load scenario '${scenarioId}'.`);
  }

  async loadScenario(): Promise<ProjectBundle> {
    return { graphs: this.bundles };
  }

  /** The active graph's bundle (back-compat for callers that want one graph). */
  getBundle(): ScenarioBundle {
    return this.bundle;
  }

  /** The current full graph collection (live in-memory snapshot). */
  getProject(): ProjectBundle {
    return { graphs: this.bundles };
  }

  subscribe(onTick: (tick: TickPayload) => void): () => void {
    this.subscribers.add(onTick);
    return () => this.subscribers.delete(onTick);
  }

  onBundleChange(cb: () => void): () => void {
    this.bundleSubs.add(cb);
    return () => this.bundleSubs.delete(cb);
  }

  onError(cb: (msg: string) => void): () => void {
    this.errorSubs.add(cb);
    return () => this.errorSubs.delete(cb);
  }

  // ─── Param + edit mutations ─────────────────────────────────────────────
  setParam(path: string, value: number): void {
    // path: "<nodeId>.<key>" | "e:<edgeId>.<key>"
    if (path.startsWith('e:')) {
      const rest = path.slice(2);
      const dot = rest.indexOf('.');
      if (dot < 0) return;
      const id = rest.slice(0, dot);
      const k = rest.slice(dot + 1);
      this.params.edges[id] = { ...this.params.edges[id], [k]: value };
    } else {
      const dot = path.indexOf('.');
      if (dot < 0) return;
      const id = path.slice(0, dot);
      const k = path.slice(dot + 1);
      this.params.nodes[id] = { ...this.params.nodes[id], [k]: value };
    }
    // No bundle change needed — params live in this.params, not the bundle.
    // The next tick (if running) reads the new value automatically.
  }

  /** Receive the latest tables snapshot and immediately re-resolve every cell
   *  binding into the per-graph param overlays — works while paused too, so
   *  the next tick (whenever it runs) starts from the DB's truth. */
  updateTableSnapshots(snapshots: readonly BindingTableSnapshot[] | null): void {
    this.tableSnapshots = snapshots;
    this.syncActiveToMaps();
    this.applyCellBindings();
  }

  /** Resolve cell bindings against the latest snapshots and merge the values
   *  into every graph's param map. Bound paths overwrite local setParam
   *  writes — the DB is the source of truth (a slider drag PATCHes the cell;
   *  the refreshed snapshot flows back through here). Broken bindings are
   *  skipped: the saved ParamSpec default stays in effect. No-op without
   *  snapshots or bindings. */
  private applyCellBindings(): void {
    if (!this.tableSnapshots) return;
    const res = resolveCellBindings(this.bundles, this.tableSnapshots);
    if (!res) return;
    for (const [gid, patch] of Object.entries(res.patches)) {
      const target = this.paramsByGraph[gid];
      if (target) applyBindingPatches(target, { [gid]: patch });
    }
  }

  addNode(node: GraphNode): void {
    if (this.bundle.graph.nodes.some((n) => n.id === node.id)) return;
    this.bundle = {
      ...this.bundle,
      graph: { ...this.bundle.graph, nodes: [...this.bundle.graph.nodes, node] },
    };
    this.params.nodes[node.id] = resolveInitialParams({
      ...this.bundle,
      graph: { ...this.bundle.graph, nodes: [node] },
    }).nodes[node.id];
    if (this.stocks[node.id] === undefined) this.stocks[node.id] = 0;
    this.fireBundleChange();
  }

  updateNode(node: GraphNode): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        nodes: this.bundle.graph.nodes.map((n) => (n.id === node.id ? node : n)),
      },
    };
    this.syncNodeParams(node);
    this.fireBundleChange();
  }

  /** Re-seed defaults for new ParamSpecs and drop orphan values for removed ones. */
  private syncNodeParams(node: GraphNode): void {
    const live = new Set<string>([...node.params, ...(node.gate?.params ?? [])].map((s) => s.key));
    const cur = this.params.nodes[node.id] ?? {};
    const next: Record<string, number> = {};
    for (const spec of [...node.params, ...(node.gate?.params ?? [])]) {
      next[spec.key] = cur[spec.key] ?? spec.value;
    }
    // Keep resolved values for keys not in any ParamSpec only if they're
    // engine-internal (expr). Otherwise drop orphans.
    for (const [k, v] of Object.entries(cur)) {
      if (!live.has(k) && k !== 'expr') next[k] = v;
    }
    this.params.nodes[node.id] = next;
  }

  removeNode(nodeId: string): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        nodes: this.bundle.graph.nodes.filter((n) => n.id !== nodeId),
        edges: this.bundle.graph.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
      },
    };
    delete this.stocks[nodeId];
    delete this.params.nodes[nodeId];
    this.fireBundleChange();
  }

  duplicateNode(nodeId: string): string | null {
    const src = this.bundle.graph.nodes.find((n) => n.id === nodeId);
    if (!src) return null;
    // Generate a unique id: <src>_copy, <src>_copy2, ...
    let newId = `${nodeId}_copy`;
    let n = 2;
    while (this.bundle.graph.nodes.some((m) => m.id === newId)) {
      newId = `${nodeId}_copy${n++}`;
    }
    const clone: GraphNode = structuredClone(src);
    clone.id = newId;
    clone.label = `${src.label} copy`;
    clone.position = {
      x: (src.position?.x ?? 0) + 48,
      y: (src.position?.y ?? 0) + 48,
    };
    this.addNode(clone);
    return newId;
  }

  renameNode(oldId: string, newId: string): boolean {
    if (newId === oldId) return true;
    if (!this.bundle.graph.nodes.some((n) => n.id === oldId)) return false;
    if (this.bundle.graph.nodes.some((n) => n.id === newId)) return false;
    const rewriteNode = (id: string): string => (id === oldId ? newId : id);
    this.bundle = {
      ...this.bundle,
      graph: {
        nodes: this.bundle.graph.nodes.map((n) =>
          n.id === oldId ? { ...n, id: newId } : n,
        ),
        edges: this.bundle.graph.edges.map((e) => ({
          ...e,
          source: rewriteNode(e.source),
          target: rewriteNode(e.target),
          // Edge ids are `<src>__<tgt>` by convention; rewrite so a follow-up
          // updateEdge match still works.
          id: e.id.replace(oldId, newId),
        })),
      },
      metrics: this.bundle.metrics.map((m) =>
        m.nodeId === oldId ? { ...m, nodeId: newId } : m,
      ),
      initialStocks: this.bundle.initialStocks
        ? Object.fromEntries(
            Object.entries(this.bundle.initialStocks).map(([k, v]) => [
              k === oldId ? newId : k,
              v,
            ]),
          )
        : this.bundle.initialStocks,
    };
    // Migrate runtime param/stock maps too.
    if (this.params.nodes[oldId] !== undefined) {
      this.params.nodes[newId] = this.params.nodes[oldId];
      delete this.params.nodes[oldId];
    }
    if (this.stocks[oldId] !== undefined) {
      this.stocks[newId] = this.stocks[oldId];
      delete this.stocks[oldId];
    }
    this.fireBundleChange();
    return true;
  }

  addEdge(edge: GraphEdge): void {
    if (this.bundle.graph.edges.some((e) => e.id === edge.id)) return;
    this.bundle = {
      ...this.bundle,
      graph: { ...this.bundle.graph, edges: [...this.bundle.graph.edges, edge] },
    };
    this.params.edges[edge.id] = resolveInitialParams({
      ...this.bundle,
      graph: { ...this.bundle.graph, edges: [edge] },
    }).edges[edge.id];
    this.fireBundleChange();
  }

  updateEdge(edge: GraphEdge): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        edges: this.bundle.graph.edges.map((e) => (e.id === edge.id ? edge : e)),
      },
    };
    const live = new Set(edge.params.map((s) => s.key));
    const cur = this.params.edges[edge.id] ?? {};
    const next: Record<string, number> = {};
    for (const spec of edge.params) next[spec.key] = cur[spec.key] ?? spec.value;
    for (const [k, v] of Object.entries(cur)) {
      if (!live.has(k)) next[k] = v; // keep engine-internal / connection-source keys
    }
    this.params.edges[edge.id] = next;
    this.fireBundleChange();
  }

  removeEdge(edgeId: string): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        edges: this.bundle.graph.edges.filter((e) => e.id !== edgeId),
      },
    };
    delete this.params.edges[edgeId];
    this.fireBundleChange();
  }

  renameEdge(oldId: string, newId: string): boolean {
    if (newId === oldId) return true;
    if (!this.bundle.graph.edges.some((e) => e.id === oldId)) return false;
    if (this.bundle.graph.edges.some((e) => e.id === newId)) return false;
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        edges: this.bundle.graph.edges.map((e) => {
          if (e.id !== oldId) {
            // Rewrite derive connections that referenced the old edge id.
            const conns = e.connections?.map((c) =>
              c.kind === 'derive' && c.sourceParam === oldId
                ? { ...c, sourceParam: newId }
                : c,
            );
            return conns && conns !== e.connections ? { ...e, connections: conns } : e;
          }
          return { ...e, id: newId };
        }),
      },
    };
    if (this.params.edges[oldId] !== undefined) {
      this.params.edges[newId] = this.params.edges[oldId];
      delete this.params.edges[oldId];
    }
    this.fireBundleChange();
    return true;
  }

  setEdgeGroup(edgeId: string, group: string | undefined): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        edges: this.bundle.graph.edges.map((e) =>
          e.id === edgeId ? { ...e, group } : e,
        ),
      },
    };
    this.fireBundleChange();
  }

  updateEdgeHandles(
    edgeId: string,
    next: { source?: string; target?: string; sourceHandle?: string; targetHandle?: string },
  ): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        edges: this.bundle.graph.edges.map((e) => {
          if (e.id !== edgeId) return e;
          const updated: GraphEdge = { ...e };
          if (next.source !== undefined) updated.source = next.source;
          if (next.target !== undefined) updated.target = next.target;
          // `undefined` handle resets the side to RF's default (closest).
          // We explicitly assign so a switch from "right" to "left" persists.
          if ('sourceHandle' in next) updated.sourceHandle = next.sourceHandle;
          if ('targetHandle' in next) updated.targetHandle = next.targetHandle;
          return updated;
        }),
      },
    };
    this.fireBundleChange();
  }

  setBends(edgeId: string, bends: Bend[]): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        edges: this.bundle.graph.edges.map((e) =>
          e.id === edgeId ? { ...e, bends: bends.length > 0 ? bends : undefined } : e,
        ),
      },
    };
    this.fireBundleChange();
  }

  setLabelT(edgeId: string, t: number): void {
    const clamped = Math.max(0, Math.min(1, t));
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        edges: this.bundle.graph.edges.map((e) =>
          e.id === edgeId ? { ...e, labelT: clamped } : e,
        ),
      },
    };
    this.fireBundleChange();
  }

  addGroup(group: string): void {
    const g = group.trim();
    if (!g) return;
    const cur = this.bundle.groups ?? [];
    if (cur.includes(g)) return;
    this.bundle = { ...this.bundle, groups: [...cur, g] };
    this.fireBundleChange();
  }

  addNodeGroup(group: NodeGroup): void {
    const cur = this.bundle.nodeGroups ?? [];
    if (cur.some((g) => g.id === group.id)) return;
    this.bundle = { ...this.bundle, nodeGroups: [...cur, group] };
    this.fireBundleChange();
  }

  updateNodeGroup(group: NodeGroup): void {
    const cur = this.bundle.nodeGroups ?? [];
    if (!cur.some((g) => g.id === group.id)) return;
    this.bundle = {
      ...this.bundle,
      nodeGroups: cur.map((g) => (g.id === group.id ? group : g)),
    };
    this.fireBundleChange();
  }

  removeNodeGroup(groupId: string): void {
    const cur = this.bundle.nodeGroups ?? [];
    this.bundle = {
      ...this.bundle,
      nodeGroups: cur.filter((g) => g.id !== groupId),
    };
    this.fireBundleChange();
  }

  renameGroup(oldId: string, newId: string): boolean {
    if (newId === oldId) return true;
    const cur = this.bundle.nodeGroups ?? [];
    if (!cur.some((g) => g.id === oldId)) return false;
    if (cur.some((g) => g.id === newId)) return false;
    // Membership is spatial, so renaming a group id has no cross-references to
    // rewrite — only the group's own id field changes.
    this.bundle = {
      ...this.bundle,
      nodeGroups: cur.map((g) => (g.id === oldId ? { ...g, id: newId } : g)),
    };
    this.fireBundleChange();
    return true;
  }

  setLegend(legend: LegendEntry[]): void {
    // Cosmetic only — no sim state to reset, no params/stocks to reseed. An
    // empty list is stored as undefined so we don't persist `legend: []` (the
    // sidebar then falls back to the palette-derived default).
    this.bundle = {
      ...this.bundle,
      legend: legend.length > 0 ? legend : undefined,
    };
    this.fireBundleChange();
  }

  setKnobs(knobs: KnobPin[]): void {
    // Cosmetic metadata only — the pins are references; slider drags dispatch
    // through setParam / updateTableCell. Empty list stored as undefined so
    // untouched bundles show nothing (mirrors setLegend).
    this.bundle = {
      ...this.bundle,
      knobs: knobs.length > 0 ? knobs : undefined,
    };
    this.fireBundleChange();
  }

  setWorkflowTriggers(triggers: WorkflowTrigger[]): void {
    // Declarative wiring metadata — the backend relay path is the evaluator,
    // so there is no sim state to reset. Empty list stored as undefined so
    // untouched bundles show nothing (mirrors setLegend/setKnobs).
    this.bundle = {
      ...this.bundle,
      workflowTriggers: triggers.length > 0 ? triggers : undefined,
    };
    this.fireBundleChange();
  }

  setParamKey(target: 'node' | 'edge', id: string, oldKey: string, newKey: string): void {
    if (oldKey === newKey || !newKey) return;
    // 1. Rewrite the ParamSpec key + carry its value across in the resolved params.
    if (target === 'node') {
      const node = this.bundle.graph.nodes.find((n) => n.id === id);
      if (node) {
        const rewrite = (specs: ParamSpec[]) =>
          specs.map((s) => (s.key === oldKey ? { ...s, key: newKey } : s));
        const updated: GraphNode = {
          ...node,
          params: rewrite(node.params),
          gate: node.gate ? { ...node.gate, params: rewrite(node.gate.params) } : undefined,
          source: node.source?.rateParam === oldKey ? { ...node.source, rateParam: newKey } : node.source,
        };
        this.updateNodeSilent(updated);
        const vals = this.params.nodes[id] ?? {};
        if (oldKey in vals) {
          const { [oldKey]: _drop, ...rest } = vals;
          void _drop;
          this.params.nodes[id] = { ...rest, [newKey]: vals[oldKey] };
        }
      }
    } else {
      const edge = this.bundle.graph.edges.find((e) => e.id === id);
      if (edge) {
        const updated: GraphEdge = {
          ...edge,
          params: edge.params.map((s) => (s.key === oldKey ? { ...s, key: newKey } : s)),
        };
        this.updateEdgeSilent(updated);
        const vals = this.params.edges[id] ?? {};
        if (oldKey in vals) {
          const { [oldKey]: _drop, ...rest } = vals;
          void _drop;
          this.params.edges[id] = { ...rest, [newKey]: vals[oldKey] };
        }
      }
    }
    // 2. Rewrite references across the bundle: EdgeConnection.sourceParam,
    //    metric `param:`/`state:` quantities.
    this.bundle = rewriteParamReferences(this.bundle, oldKey, newKey, target === 'node' ? id : undefined);
    this.fireBundleChange();
  }

  /** updateNode without firing bundle change (for internal multi-step updates). */
  private updateNodeSilent(node: GraphNode): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        nodes: this.bundle.graph.nodes.map((n) => (n.id === node.id ? node : n)),
      },
    };
  }
  private updateEdgeSilent(edge: GraphEdge): void {
    this.bundle = {
      ...this.bundle,
      graph: {
        ...this.bundle.graph,
        edges: this.bundle.graph.edges.map((e) => (e.id === edge.id ? edge : e)),
      },
    };
  }

  addMetric(metric: MetricSpec): void {
    if (this.bundle.metrics.some((m) => m.key === metric.key)) return;
    this.bundle = { ...this.bundle, metrics: [...this.bundle.metrics, metric] };
    this.fireBundleChange();
  }
  updateMetric(metric: MetricSpec): void {
    this.bundle = {
      ...this.bundle,
      metrics: this.bundle.metrics.map((m) => (m.key === metric.key ? metric : m)),
    };
    this.fireBundleChange();
  }
  removeMetric(key: string): void {
    this.bundle = { ...this.bundle, metrics: this.bundle.metrics.filter((m) => m.key !== key) };
    this.fireBundleChange();
  }
  renameMetric(oldKey: string, newKey: string): void {
    if (oldKey === newKey) return;
    if (this.bundle.metrics.some((m) => m.key === newKey)) return;
    this.bundle = {
      ...this.bundle,
      metrics: this.bundle.metrics.map((m) => (m.key === oldKey ? { ...m, key: newKey } : m)),
    };
    this.fireBundleChange();
  }

  replaceBundle(bundle: ScenarioBundle): void {
    this.bundle = bundle;
    this.params = resolveInitialParams(bundle);
    this.stocks = { ...(bundle.initialStocks ?? {}) };
    this.step = 0;
    this.lastState = undefined;
    this.fireBundleChange();
  }

  exportBundle(): ScenarioBundle {
    return this.bundle;
  }

  reset(): void {
    this.step = 0;
    // Reset every graph's stocks + prevState, then reload the active view.
    for (const b of this.bundles) {
      this.stocksByGraph[b.id] = { ...(b.initialStocks ?? {}) };
      this.prevStateByGraph[b.id] = undefined;
    }
    this.loadActiveFromMaps();
  }

  start(): void {
    if (this.timer !== null) return;
    const period = 1000 / this.hz;
    this.timer = setInterval(() => this.pump(), period);
  }

  stop(): void {
    if (this.timer === null) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  dispose(): void {
    this.stop();
    this.subscribers.clear();
    this.bundleSubs.clear();
  }

  // ─── Multi-graph (graph collection) ──────────────────────────────────────
  graphIds(): string[] {
    return this.bundles.map((b) => b.id);
  }

  activeGraphId(): string {
    return this.activeId;
  }

  setActiveGraph(graphId: string): void {
    if (graphId === this.activeId) return;
    if (!this.bundles.some((b) => b.id === graphId)) return;
    // Save the outgoing graph's live state, then load the new one.
    this.syncActiveToMaps();
    this.activeId = graphId;
    this.loadActiveFromMaps();
    this.fireBundleChange();
  }

  addGraph(name: string): string {
    const id = uniqueGraphId(this.bundles, slugifyGraph(name));
    const nb = emptyGraphBundle(id, name);
    this.bundles = [...this.bundles, nb];
    this.paramsByGraph[id] = resolveInitialParams(nb);
    this.stocksByGraph[id] = {};
    this.prevStateByGraph[id] = undefined;
    this.fireBundleChange();
    return id;
  }

  removeGraph(graphId: string): void {
    if (graphId === MAIN_GRAPH_ID) return; // Main is non-removable
    if (!this.bundles.some((b) => b.id === graphId)) return;
    this.syncActiveToMaps();
    this.bundles = this.bundles.filter((b) => b.id !== graphId);
    delete this.paramsByGraph[graphId];
    delete this.stocksByGraph[graphId];
    delete this.prevStateByGraph[graphId];
    if (this.activeId === graphId) {
      this.activeId = MAIN_GRAPH_ID;
      this.loadActiveFromMaps();
    }
    this.fireBundleChange();
  }

  renameGraph(graphId: string, name: string): string {
    const idx = this.bundles.findIndex((b) => b.id === graphId);
    if (idx < 0) return graphId;
    this.syncActiveToMaps();
    const newId =
      graphId === MAIN_GRAPH_ID
        ? MAIN_GRAPH_ID
        : uniqueGraphId(
            this.bundles.filter((b) => b.id !== graphId),
            slugifyGraph(name),
          );
    const renamed: ScenarioBundle = { ...this.bundles[idx], id: newId, label: name };
    this.bundles = this.bundles.map((b) => (b.id === graphId ? renamed : b));
    // Re-key the per-graph state maps if the id changed.
    if (newId !== graphId) {
      this.paramsByGraph[newId] = this.paramsByGraph[graphId];
      this.stocksByGraph[newId] = this.stocksByGraph[graphId];
      this.prevStateByGraph[newId] = this.prevStateByGraph[graphId];
      delete this.paramsByGraph[graphId];
      delete this.stocksByGraph[graphId];
      delete this.prevStateByGraph[graphId];
    }
    if (this.activeId === graphId) {
      this.activeId = newId;
      this.loadActiveFromMaps();
    }
    this.fireBundleChange();
    return newId;
  }

  // ─── Internal ───────────────────────────────────────────────────────────
  /** Write the active graph's live state back into the per-graph maps + the
   *  bundles array. Called before any operation that reads the maps uniformly
   *  (pump, setActiveGraph, add/remove/rename) so the active entry is fresh. */
  private syncActiveToMaps(): void {
    const idx = this.bundles.findIndex((b) => b.id === this.activeId);
    if (idx < 0) return;
    this.bundles = this.bundles.map((b, i) => (i === idx ? this.bundle : b));
    this.paramsByGraph[this.activeId] = this.params;
    this.stocksByGraph[this.activeId] = this.stocks;
    this.prevStateByGraph[this.activeId] = this.lastState;
  }

  /** Load the active graph's state from the maps into the instance fields. */
  private loadActiveFromMaps(): void {
    const b = this.bundles.find((x) => x.id === this.activeId) ?? this.bundles[0];
    this.activeId = b.id;
    this.bundle = b;
    this.params = this.paramsByGraph[this.activeId];
    this.stocks = this.stocksByGraph[this.activeId];
    this.lastState = this.prevStateByGraph[this.activeId];
  }

  private pump(): void {
    this.step += 1;
    // Sync the active graph's live state into the maps so every graph reads
    // uniformly, then co-tick all graphs into one merged tick.
    this.syncActiveToMaps();
    // Cell bindings resolve fresh each tick — the snapshots may have been
    // refreshed (tables poll / a PATCH splice) since the last pump.
    this.applyCellBindings();
    const stateByGraph: Record<string, PerGraphSimState> = {};
    for (const b of this.bundles) {
      stateByGraph[b.id] = {
        params: this.paramsByGraph[b.id],
        stocks: this.stocksByGraph[b.id],
        prevState: this.prevStateByGraph[b.id],
      };
    }
    let merged: TickPayload;
    let perGraph: TickPayload[];
    try {
      ({ merged, perGraph } = computeProjectTickSplit(this.bundles, stateByGraph, this.step));
    } catch (e) {
      // CycleError / ExprError — surface to the UI and auto-stop.
      const msg = e instanceof Error ? e.message : String(e);
      for (const fn of this.errorSubs) fn(msg);
      this.stop();
      return;
    }
    // Advance each graph's stocks + prevState from its per-graph tick.
    for (let i = 0; i < this.bundles.length; i++) {
      const id = this.bundles[i].id;
      const tick = perGraph[i];
      const next: Record<string, Record<string, number>> = {};
      const stocks = { ...this.stocksByGraph[id] };
      for (const [nid, v] of Object.entries(tick.nodeValues)) {
        stocks[nid] = v.stock;
        if (v.state) next[nid] = v.state;
      }
      this.stocksByGraph[id] = stocks;
      this.prevStateByGraph[id] = Object.keys(next).length ? next : undefined;
    }
    // Reload the active graph's advanced stocks/prevState into the live view.
    this.loadActiveFromMaps();
    for (const fn of this.subscribers) fn(merged);
  }

  private fireBundleChange(): void {
    // Keep the active entry consistent before notifying (cheap; idempotent).
    this.syncActiveToMaps();
    for (const fn of this.bundleSubs) fn();
  }
}

// ─── Multi-graph helpers (module-local, domain-free) ───────────────────────

/** Force a bundle into the Main-graph shape (id/label). Used when wrapping a
 *  legacy single-graph bundle as a one-graph project. */
function asMain(bundle: ScenarioBundle): ScenarioBundle {
  return bundle.id === MAIN_GRAPH_ID && bundle.label === 'Main'
    ? bundle
    : { ...bundle, id: MAIN_GRAPH_ID, label: 'Main' };
}

/** A fresh empty Main graph (no nodes/edges/metrics). Exported for useSim's
 * load-failure fallback: the app renders an empty canvas with the error shown
 * inside it instead of a full-screen block. */
export function emptyMainBundle(): ScenarioBundle {
  return emptyGraphBundle(MAIN_GRAPH_ID, 'Main');
}

/** A fresh empty graph bundle (used by addGraph + the Main fallback). */
function emptyGraphBundle(id: string, label: string): ScenarioBundle {
  return { id, label, graph: { nodes: [], edges: [] }, metrics: [], initialParams: {} };
}

/** Slugify a graph display name into a filesystem-safe id. Mirrors the
 *  Python `_slugify_graph`. */
function slugifyGraph(name: string): string {
  const slug = name.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
  return slug || 'graph';
}

/** Return `base` if free among the bundles' ids, else `base-2`, `base-3`, …
 *  Never hands out MAIN_GRAPH_ID to a non-Main graph. */
function uniqueGraphId(bundles: ScenarioBundle[], base: string): string {
  if (base === MAIN_GRAPH_ID) base = `${base}-2`;
  const existing = new Set(bundles.map((b) => b.id));
  if (!existing.has(base)) return base;
  let n = 2;
  while (existing.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/** Fetch a static scenario's Main graph: prefer `graphs/main.json`, fall back
 *  to the legacy top-level `graph.json`. Returns null if neither is JSON. */
async function fetchMainGraph(dir: string): Promise<ScenarioBundle | null> {
  for (const name of ['graphs/main.json', 'graph.json']) {
    try {
      const res = await fetch(`${dir}${name}`);
      if (!res.ok) continue;
      const ct = res.headers?.get('content-type') ?? '';
      if (ct && !ct.includes('application/json')) continue;
      return (await res.json()) as ScenarioBundle;
    } catch {
      continue;
    }
  }
  return null;
}

/** A LocalAdapter shell that loads its bundle on first loadScenario call.
 *  Loading is async (localStorage bundle first, then a served scenario dir),
 *  so this wraps the load. The editor methods throw clearly if called before
 *  loadScenario resolves (they won't be — the UI mounts after load). */
export class LazyLocalAdapter implements BackendAdapter {
  private inner: LocalAdapter | null = null;
  private pendingAdapter: Promise<LocalAdapter> | null = null;
  private bufferedTickSubs = new Set<(tick: TickPayload) => void>();
  private bufferedBundleSubs = new Set<() => void>();
  private readonly startSignal = { started: false };
  /** Snapshots pushed before the inner adapter materialised — replayed on
   *  load so cell bindings resolve from the start. */
  private pendingSnapshots: readonly BindingTableSnapshot[] | null = null;
  constructor(private readonly scenarioId: string, private readonly hz: number = 4) {}

  private ensureLoad(): Promise<LocalAdapter> {
    if (this.inner) return Promise.resolve(this.inner);
    if (!this.pendingAdapter) {
      this.pendingAdapter = (async () => {
        const storage = getDefaultStorage();
        const saved = storage.loadBundle(this.scenarioId);
        if (saved) return new LocalAdapter(saved, { hz: this.hz });
        return LocalAdapter.fromScenarioDir(this.scenarioId, { hz: this.hz });
      })().then(
        (a) => {
          this.inner = a;
          for (const fn of this.bufferedTickSubs) a.subscribe(fn);
          for (const fn of this.bufferedBundleSubs) a.onBundleChange(fn);
          this.bufferedTickSubs.clear();
          this.bufferedBundleSubs.clear();
          if (this.startSignal.started) a.start();
          if (this.pendingSnapshots) a.updateTableSnapshots(this.pendingSnapshots);
          return a;
        },
      );
    }
    return this.pendingAdapter;
  }

  async loadScenario(): Promise<ProjectBundle> {
    const a = await this.ensureLoad();
    return a.loadScenario();
  }

  subscribe(onTick: (tick: TickPayload) => void): () => void {
    if (this.inner) return this.inner.subscribe(onTick);
    this.bufferedTickSubs.add(onTick);
    return () => {
      this.bufferedTickSubs.delete(onTick);
    };
  }
  onBundleChange(cb: () => void): () => void {
    if (this.inner) return this.inner.onBundleChange(cb);
    this.bufferedBundleSubs.add(cb);
    return () => {
      this.bufferedBundleSubs.delete(cb);
    };
  }
  onError(cb: (msg: string) => void): () => void {
    if (this.inner) return this.inner.onError(cb);
    return () => {};
  }
  setParam(path: string, value: number): void {
    this.inner?.setParam(path, value);
  }
  updateTableSnapshots(snapshots: readonly BindingTableSnapshot[] | null): void {
    // Remember even pre-load pushes so the inner adapter starts from the
    // latest truth the moment it materialises.
    this.pendingSnapshots = snapshots;
    this.inner?.updateTableSnapshots?.(snapshots);
  }
  setParamKey(target: 'node' | 'edge', id: string, oldKey: string, newKey: string): void {
    this.inner?.setParamKey(target, id, oldKey, newKey);
  }
  addNode(node: GraphNode): void {
    this.inner?.addNode(node);
  }
  updateNode(node: GraphNode): void {
    this.inner?.updateNode(node);
  }
  removeNode(nodeId: string): void {
    this.inner?.removeNode(nodeId);
  }
  duplicateNode(nodeId: string): string | null {
    return this.inner?.duplicateNode(nodeId) ?? null;
  }
  renameNode(oldId: string, newId: string): boolean {
    return this.inner?.renameNode(oldId, newId) ?? false;
  }
  addEdge(edge: GraphEdge): void {
    this.inner?.addEdge(edge);
  }
  updateEdge(edge: GraphEdge): void {
    this.inner?.updateEdge(edge);
  }
  removeEdge(edgeId: string): void {
    this.inner?.removeEdge(edgeId);
  }
  renameEdge(oldId: string, newId: string): boolean {
    return this.inner?.renameEdge(oldId, newId) ?? false;
  }
  setEdgeGroup(edgeId: string, group: string | undefined): void {
    this.inner?.setEdgeGroup(edgeId, group);
  }
  updateEdgeHandles(
    edgeId: string,
    next: { source?: string; target?: string; sourceHandle?: string; targetHandle?: string },
  ): void {
    this.inner?.updateEdgeHandles?.(edgeId, next);
  }
  setBends(edgeId: string, bends: Bend[]): void {
    this.inner?.setBends?.(edgeId, bends);
  }
  setLabelT(edgeId: string, t: number): void {
    this.inner?.setLabelT?.(edgeId, t);
  }
  addGroup(group: string): void {
    this.inner?.addGroup(group);
  }
  addNodeGroup(group: NodeGroup): void {
    this.inner?.addNodeGroup(group);
  }
  updateNodeGroup(group: NodeGroup): void {
    this.inner?.updateNodeGroup(group);
  }
  removeNodeGroup(groupId: string): void {
    this.inner?.removeNodeGroup(groupId);
  }
  renameGroup(oldId: string, newId: string): boolean {
    return this.inner?.renameGroup(oldId, newId) ?? false;
  }
  addMetric(metric: MetricSpec): void {
    this.inner?.addMetric(metric);
  }
  updateMetric(metric: MetricSpec): void {
    this.inner?.updateMetric(metric);
  }
  removeMetric(key: string): void {
    this.inner?.removeMetric(key);
  }
  renameMetric(oldKey: string, newKey: string): void {
    this.inner?.renameMetric?.(oldKey, newKey);
  }
  setLegend(legend: LegendEntry[]): void {
    this.inner?.setLegend?.(legend);
  }
  setKnobs(knobs: KnobPin[]): void {
    this.inner?.setKnobs?.(knobs);
  }

  setWorkflowTriggers(triggers: WorkflowTrigger[]): void {
    this.inner?.setWorkflowTriggers?.(triggers);
  }
  replaceBundle(bundle: ScenarioBundle): void {
    this.inner?.replaceBundle(bundle);
  }
  exportBundle(): ScenarioBundle {
    return this.inner?.exportBundle() ?? this.inner?.getBundle() ?? ({} as ScenarioBundle);
  }
  // ─── Multi-graph proxies ─────────────────────────────────────────────────
  graphIds(): string[] {
    return this.inner?.graphIds() ?? [MAIN_GRAPH_ID];
  }
  activeGraphId(): string {
    return this.inner?.activeGraphId() ?? MAIN_GRAPH_ID;
  }
  getProject(): ProjectBundle | null {
    return this.inner?.getProject?.() ?? null;
  }
  setActiveGraph(graphId: string): void {
    this.inner?.setActiveGraph?.(graphId);
  }
  addGraph(name: string): string {
    return this.inner?.addGraph?.(name) ?? MAIN_GRAPH_ID;
  }
  removeGraph(graphId: string): void {
    this.inner?.removeGraph?.(graphId);
  }
  renameGraph(graphId: string, name: string): string {
    return this.inner?.renameGraph?.(graphId, name) ?? graphId;
  }
  reset(): void {
    this.inner?.reset();
  }
  start(): void {
    this.startSignal.started = true;
    this.inner?.start();
  }
  stop(): void {
    this.startSignal.started = false;
    this.inner?.stop();
  }
  dispose(): void {
    this.inner?.dispose();
  }
}

/** Rewrite references to a renamed param key across the bundle.
 *  - EdgeConnection.sourceParam when it equals oldKey AND the connection's
 *    edge touches the renamed node (or any edge if target was an edge).
 *  - source.rateParam on nodes (only the renamed node).
 *  - metric `param:<key>` / `state:<key>` NodeQuantity strings — only when
 *    the metric's nodeId matches (per-node metric) or target is unspecified
 *    (global metric, rewrite conservatively when the key is unambiguous). */
function rewriteParamReferences(
  bundle: ScenarioBundle,
  oldKey: string,
  newKey: string,
  nodeId: string | undefined,
): ScenarioBundle {
  const rewriteConn = (c: EdgeConnection): EdgeConnection =>
    c.sourceParam === oldKey ? { ...c, sourceParam: newKey } : c;
  let graph = bundle.graph;
  // Edges: rewrite connections + legacy derive.priceParam/rateParam on edges
  // whose params include the renamed key (target was an edge) OR whose source
  // node is the renamed node.
  graph = {
    ...graph,
    edges: graph.edges.map((e) => {
      const conns = e.connections?.map(rewriteConn);
      const derive =
        e.derive && (e.derive.priceParam === oldKey || e.derive.rateParam === oldKey)
          ? {
              ...e.derive,
              priceParam: e.derive.priceParam === oldKey ? newKey : e.derive.priceParam,
              rateParam: e.derive.rateParam === oldKey ? newKey : e.derive.rateParam,
            }
          : e.derive;
      if (conns === e.connections && derive === e.derive) return e;
      return { ...e, connections: conns, derive };
    }),
  };
  // Nodes: rewrite source.rateParam on the renamed node.
  if (nodeId) {
    graph = {
      ...graph,
      nodes: graph.nodes.map((n) =>
        n.id === nodeId && n.source?.rateParam === oldKey
          ? { ...n, source: { ...n.source, rateParam: newKey } }
          : n,
      ),
    };
  }
  const metrics = bundle.metrics.map((m) => {
    if (nodeId && m.nodeId && m.nodeId !== nodeId) return m; // other node's metric
    const expr = m.expr
      .replace(new RegExp(`\\bparams\\.${oldKey}\\b`, 'g'), `params.${newKey}`)
      .replace(new RegExp(`\\bself\\.${oldKey}\\b`, 'g'), `self.${newKey}`);
    return { ...m, expr };
  });
  return { ...bundle, graph, metrics };
}
